/**
 * Round-index freshness (PRD #330, issue #334) — framework-free, so the timing
 * rules are unit-testable with fake timers and a fake fetcher.
 *
 *  - In-memory cache per round (stale-while-revalidate): a visit renders the
 *    last-seen index instantly and revalidates in the background.
 *  - Revalidation happens on start (mount), on `refresh()` (manual reload),
 *    when the tab becomes visible / regains focus, and every 15 s ONLY while
 *    the tab is visible — a hidden tab sends nothing.
 *  - Every revalidation sends the cached `ETag` as `If-None-Match`. A `304` is
 *    a no-op; a `200` replaces the index and — when it replaced an index that
 *    was already on screen — calls `onChange`, which the Tablero uses to reload
 *    the report so the KPI cards agree with the table.
 *  - At most one request in flight; answers arriving after `stop()` are dropped.
 */

import type { ExpedienteIndexRow } from "../../api/client";

export const PROBE_INTERVAL_MS = 15_000;

export type IndexEntry = { items: ExpedienteIndexRow[]; etag: string | null };

export type IndexFetchResult =
  | { status: "not-modified" }
  | { status: "ok"; etag: string | null; items: ExpedienteIndexRow[] };

/** Loads a round's index, conditionally on `ifNoneMatch` when given. */
export type IndexFetcher = (
  roundId: string,
  ifNoneMatch: string | null,
) => Promise<IndexFetchResult>;

/** Tab visibility: current state + a subscription fired when the tab becomes
 * visible again or regains focus. */
export type Visibility = {
  isVisible: () => boolean;
  subscribe: (onVisible: () => void) => () => void;
};

export class RoundIndexCache {
  private readonly entries = new Map<string, IndexEntry>();

  get(roundId: string): IndexEntry | undefined {
    return this.entries.get(roundId);
  }

  set(roundId: string, entry: IndexEntry): void {
    this.entries.set(roundId, entry);
  }
}

export type RoundIndexSyncOptions = {
  roundId: string;
  fetcher: IndexFetcher;
  cache: RoundIndexCache;
  visibility: Visibility;
  /** A (new) index to render. */
  onData: (items: ExpedienteIndexRow[]) => void;
  /** A 200 replaced an index that was already shown (not the first load). */
  onChange: () => void;
  onError?: (error: unknown) => void;
  intervalMs?: number;
};

export type RoundIndexSync = {
  /** Revalidate now (manual reload); no-op while a request is in flight. */
  refresh: () => void;
  stop: () => void;
};

export function startRoundIndexSync({
  roundId,
  fetcher,
  cache,
  visibility,
  onData,
  onChange,
  onError,
  intervalMs = PROBE_INTERVAL_MS,
}: RoundIndexSyncOptions): RoundIndexSync {
  let stopped = false;
  let inFlight = false;

  const cached = cache.get(roundId);
  if (cached) onData(cached.items);

  async function revalidate(): Promise<void> {
    if (stopped || inFlight) return;
    inFlight = true;
    const previous = cache.get(roundId);
    try {
      const result = await fetcher(roundId, previous?.etag ?? null);
      if (stopped || result.status === "not-modified") return;
      cache.set(roundId, { items: result.items, etag: result.etag });
      onData(result.items);
      if (previous) onChange();
    } catch (error) {
      if (!stopped) onError?.(error);
    } finally {
      inFlight = false;
    }
  }

  const refresh = () => {
    void revalidate();
  };

  const timer = setInterval(() => {
    if (visibility.isVisible()) refresh();
  }, intervalMs);
  const unsubscribe = visibility.subscribe(refresh);

  refresh();

  return {
    refresh,
    stop() {
      stopped = true;
      clearInterval(timer);
      unsubscribe();
    },
  };
}

/** The browser's visibility: `document.visibilityState` + `visibilitychange`
 * (to visible) and window `focus`. Both may fire together on a tab switch; the
 * in-flight guard collapses them into one request. */
export const documentVisibility: Visibility = {
  isVisible: () => document.visibilityState === "visible",
  subscribe(onVisible) {
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") onVisible();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("focus", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("focus", onVisible);
    };
  },
};
